"use client";

import { cn } from "@matsuricon/ui/utils";
import { useEffect, useState } from "react";

const SECOND = 1_000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
const COUNTDOWN_ACCENTS = ["text-primary", "text-brand-secondary", "text-brand-accent", "text-primary"] as const;

type ConventionCountdownProps = {
  startsAt: string;
  endsAt: string;
  initialNow: number;
};

type CountdownValue = {
  label: "Days" | "Hours" | "Minutes" | "Seconds";
  value: string;
};

function getCountdownValues(remaining: number): CountdownValue[] {
  return [
    { label: "Days", value: String(Math.floor(remaining / DAY)) },
    { label: "Hours", value: String(Math.floor((remaining % DAY) / HOUR)).padStart(2, "0") },
    { label: "Minutes", value: String(Math.floor((remaining % HOUR) / MINUTE)).padStart(2, "0") },
    { label: "Seconds", value: String(Math.floor((remaining % MINUTE) / SECOND)).padStart(2, "0") },
  ];
}

export function ConventionCountdown({ startsAt, endsAt, initialNow }: ConventionCountdownProps) {
  const [now, setNow] = useState(initialNow);
  const startTime = Date.parse(startsAt);
  const endTime = Date.parse(endsAt);

  useEffect(() => {
    const currentTime = Date.now();
    setNow(currentTime);

    if (currentTime >= endTime) return;

    const intervalId = window.setInterval(() => {
      const nextTime = Date.now();
      setNow(nextTime);

      if (nextTime >= endTime) window.clearInterval(intervalId);
    }, SECOND);

    return () => window.clearInterval(intervalId);
  }, [endTime]);

  if (now >= endTime) {
    return (
      <div role="status" className="mt-7 font-sans text-xl font-semibold text-inverse-foreground sm:text-2xl">
        See you next year
      </div>
    );
  }

  if (now >= startTime) {
    return (
      <div role="status" className="mt-7 font-sans text-xl font-semibold text-inverse-foreground sm:text-2xl">
        Matsuricon is live
      </div>
    );
  }

  const values = getCountdownValues(Math.max(0, startTime - now));
  const accessibleTime = values.map(({ label, value }) => `${value} ${label.toLowerCase()}`).join(", ");

  return (
    <div className="mt-7 w-full max-w-xl">
      <p className="text-style-overline text-brand-gold">Convention starts in</p>
      <div
        aria-label={`Countdown to Matsuricon: ${accessibleTime}`}
        className="relative mt-3 overflow-hidden rounded-2xl border border-border bg-card text-card-foreground shadow-panel"
        role="timer"
      >
        <dl className="grid grid-cols-4">
          {values.map(({ label, value }, index) => (
            <div
              className={cn(
                "relative flex min-w-0 flex-col px-1 py-3 sm:px-4 sm:py-4",
                index > 0 && "border-l border-border/70",
              )}
              data-countdown-unit=""
              key={label}
            >
              <dt className="order-2 mt-1 font-sans text-[0.625rem] text-muted-foreground sm:text-xs">{label}</dt>
              <dd
                className={cn(
                  "order-1 font-sans text-2xl font-bold tabular-nums sm:text-3xl",
                  COUNTDOWN_ACCENTS[index],
                )}
              >
                {value}
              </dd>
            </div>
          ))}
        </dl>
      </div>
    </div>
  );
}
